import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";

/**
 * Reusable confirmation dialog for destructive actions.
 *
 * Props:
 *   open        boolean
 *   onCancel    () => void
 *   onConfirm   () => void | Promise<void>
 *   title       string
 *   description string
 *   confirmLabel string  (default: "Delete")
 *   loading     boolean  (disables buttons while in-flight)
 */
export default function ConfirmDialog({
  open,
  onCancel,
  onConfirm,
  title = "Are you sure?",
  description = "This action cannot be undone.",
  confirmLabel = "Delete",
  loading = false,
}) {
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !loading) onCancel(); }}>
      <DialogContent className="max-w-sm" data-testid="confirm-dialog">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 w-9 h-9 rounded-full bg-red-100 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <DialogTitle className="text-[#032352] font-heading font-bold text-base">
              {title}
            </DialogTitle>
            <DialogDescription className="text-[#5B5C5F] text-sm mt-1">
              {description}
            </DialogDescription>
          </div>
        </div>
        <div className="flex gap-2 justify-end mt-2">
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={loading}
            data-testid="confirm-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={loading}
            data-testid="confirm-delete-btn"
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {loading ? "Deleting…" : confirmLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
